import { t } from "@lingui/macro";
import {
  AllTaskIdsDocument,
  type AllTaskIdsQuery,
  type AllTaskIdsQueryVariables,
  type ListingClientTaskFragment,
  ListingClientTasksDocument,
  type ListingClientTasksQuery,
  type ListingClientTasksQueryVariables,
  type ListingInternalTaskFragment,
  ListingInternalTasksDocument,
  type ListingInternalTasksQuery,
  type ListingInternalTasksQueryVariables,
  type TasksForScopedUsersQuery,
  type TasksQuery,
  TaskWhereColumn,
} from "@src/__generated__/graphql";
import { TaskModel } from "@src/components/modules/resource-planning/tasks/listing/models";
import { TaskListingBulkActionsStore } from "@src/components/modules/resource-planning/tasks/listing/store/bulk-actions";
import { TaskListingColumnsStore } from "@src/components/modules/resource-planning/tasks/listing/store/columns";
import { TaskListingFiltersStore } from "@src/components/modules/resource-planning/tasks/listing/store/filters";
import { TASK_ID_QUERY_KEY } from "@src/components/widgets/Modals/ModalCommunication/CommunicationModalHeader";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable, observable } from "mobx";

// NOTE: Just for legacy code sake, after we change all task components in linked tasks tab or notifications we can remove this
export type TaskType = NonNullable<
  TasksQuery["tasks"] | TasksForScopedUsersQuery["tasks"]
>["data"][0];
export type InternalTaskType = NonNullable<TasksQuery["tasks"]>["data"][0];
export type ScopedTaskType = NonNullable<
  TasksForScopedUsersQuery["tasks"]
>["data"][0];

export class TasksListingStore {
  @observable.ref
  horizontallyScrolled = false;

  @observable
  tasks: TaskModel[] = [];

  filtersStore: TaskListingFiltersStore;
  bulkActionsStore: TaskListingBulkActionsStore;
  columnsStore: TaskListingColumnsStore;

  private tasksFetcher = new FetchHelper<
    ListingInternalTasksQuery,
    ListingInternalTasksQueryVariables
  >(ListingInternalTasksDocument);
  private tasksScopedFetcher = new FetchHelper<
    ListingClientTasksQuery,
    ListingClientTasksQueryVariables
  >(ListingClientTasksDocument);
  private taskIdsFetcher = new FetchHelper<
    AllTaskIdsQuery,
    AllTaskIdsQueryVariables
  >(AllTaskIdsDocument);

  constructor(private appStore: AppStore) {
    makeObservable(this);
    this.filtersStore = new TaskListingFiltersStore(appStore);
    this.bulkActionsStore = new TaskListingBulkActionsStore(appStore);
    this.columnsStore = new TaskListingColumnsStore(appStore);
  }

  @computed
  get taskCount() {
    return this.tasks.length;
  }

  @computed
  get isFetching(): boolean {
    return (
      this.tasksFetcher.isLoading.value ||
      this.tasksScopedFetcher.isLoading.value
    );
  }

  @computed
  get isFetchingTaskIds(): boolean {
    return this.taskIdsFetcher.isLoading.value;
  }

  @computed get subtasksOpen() {
    return this.tasks.every((task) => task.isOpen.value);
  }
  @action.bound
  toggleSubtasksOpen() {
    if (this.subtasksOpen) {
      for (const task of this.tasks) {
        task.isOpen.off();
      }
    } else {
      for (const task of this.tasks) {
        task.isOpen.on();
      }
    }
  }

  @action.bound
  resetPagination() {
    this.filtersStore.pagination.setFromPaginatorInfo({
      perPage: 10,
      currentPage: 1,
      total: undefined,
      lastItem: undefined,
    });
    this.tasks = [];
  }

  @action.bound
  async fetchTasks() {
    if (!this.appStore.authStore.user?.type) return;
    const fetcher = {
      partner: this.tasksScopedFetcher,
      client: this.tasksScopedFetcher,
      internal: this.tasksFetcher,
    }[this.appStore.authStore.user.type];

    const [data, error] = await fetcher.fetch(this.filtersStore.queryParams);

    if (error || !data.tasks) return;

    this.addTasks(data.tasks.data);
    this.filtersStore.pagination.setFromPaginatorInfo(data.tasks.paginatorInfo);
    if (!this.filtersStore.pagination.canLoadMore) return;
    this.filtersStore.pagination.inc();
  }

  @action.bound
  async fetchTaskIds() {
    const [data, error] = await this.taskIdsFetcher.fetch({
      filters: {
        search: this.filtersStore.queryParams.filters.search,
        where: {
          AND: this.filtersStore.queryParams.filters.where?.AND.filter(
            (filter) =>
              !("column" in filter) ||
              filter.column !== TaskWhereColumn.OnlyParent,
          ),
        },
      },
    });

    if (error || !data.taskIds) return;
    this.bulkActionsStore.setAllIds(data.taskIds);
  }

  @action.bound addTasks(
    tasks: (ListingClientTaskFragment | ListingInternalTaskFragment)[],
  ) {
    const tasksCount = tasks.length;

    for (let i = 0; i < tasksCount; i++) {
      const task = tasks[i];
      if (!task) continue;
      if (this.tasks.some(({ id }) => id === task.id)) continue;
      this.tasks.push(new TaskModel(task, this.appStore));
    }
  }

  @action.bound
  onAddSubtask(subtaskId: TaskModel["id"]) {
    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: subtaskId,
      onChange: () => {
        this.resetPagination();
      },
      onDelete: () => {
        this.resetPagination();
      },
      onDuplicate: () => {
        this.resetPagination();
      },
      onTimeTrackingItemCreated: () => {
        this.resetPagination();
      },
    });
  }

  @action.bound
  updateTask(
    updatedTask: {
      id: TaskModel["id"];
    } & Partial<ListingInternalTaskFragment>,
  ) {
    for (const task of this.tasks) {
      for (const subtask of task.subtasks) {
        if (subtask.id !== updatedTask.id) continue;
        subtask.update(updatedTask);
        break;
      }

      if (task.id !== updatedTask.id) continue;
      task.update(updatedTask);
    }
  }

  @action.bound
  removeTask(
    taskId: TaskModel["id"],
    parentId?: NonNullable<TaskModel["parent"]>["id"],
  ) {
    if (parentId) {
      this.tasks.find(({ id }) => id === parentId)?.removeSubtask(taskId);
      return;
    }

    const taskIndex = this.tasks.findIndex(({ id }) => id === taskId);
    if (taskIndex === -1) return;
    this.tasks.splice(taskIndex, 1);
  }

  @action.bound
  handleScroll(e: React.UIEvent<HTMLDivElement>) {
    if (e.currentTarget.scrollLeft === 0 && !this.horizontallyScrolled) return;
    if (e.currentTarget.scrollLeft > 0 && this.horizontallyScrolled) return;
    this.horizontallyScrolled = e.currentTarget.scrollLeft > 0;
  }

  handleTaskClick(task: Partial<TaskModel>) {
    if (!task.id) return;

    this.appStore.taskDetailModalStore.disclosureState.onOpen({
      id: task.id,
      onChange: (updatedTask) =>
        this.updateTask({
          id: updatedTask.id,
          name: updatedTask.name,
          billable:
            "billable" in updatedTask ? updatedTask.billable : undefined,
          // commentCount: "commentCount" in updatedTask ? updatedTask.commentCount : 0,
          is_template: updatedTask.is_template,
          positions: updatedTask.positions,
          // scopedCommentCount: updatedTask.scopedCommentCount,
          priority: updatedTask.priority,
          deadline: updatedTask.deadline,
          ourWorkBudgetItem: updatedTask.ourWorkBudgetItem
            ? {
                id: updatedTask.ourWorkBudgetItem.id,
                title:
                  "title" in updatedTask.ourWorkBudgetItem
                    ? updatedTask.ourWorkBudgetItem.title
                    : "",
                project: updatedTask.ourWorkBudgetItem.project,
              }
            : undefined,
          parent: updatedTask.parent,
          task_template_group_id: updatedTask.task_template_group_id,
          // unreadCommentsCount:
          //   "unreadCommentsCount" in updatedTask
          //     ? updatedTask.unreadCommentsCount
          //     : 0,
          scopes: "scopes" in updatedTask ? updatedTask.scopes : [],
          stats: "stats" in updatedTask ? updatedTask.stats : undefined,
          status: updatedTask.status,
        }),
      onDelete: (task) => this.removeTask(task.id, task.parent?.id),
      onDuplicate: (task) => {
        this.addTasks([task]);
      },
    });
  }

  @action.bound
  handleCopyId(taskId: string) {
    navigator.clipboard.writeText(
      `${location.origin}/notifications?${TASK_ID_QUERY_KEY}=${taskId}`,
    );
    this.appStore.UIStore.toast({
      title: t`Link copied to clipboard`,
      status: "info",
      isClosable: true,
      duration: 3000,
    });
  }
}
